import { createAsyncThunk } from '@reduxjs/toolkit';
import ReactNativeBlobUtil from 'react-native-blob-util';
import { BlobServiceClient, ContainerClient } from '@azure/storage-blob';
import DeviceInfo from 'react-native-device-info';
import { backendCall, createApiCall } from '@reducers/utils';
import { isWeb, imageSource, setExpirationTimeAsTimestamp } from '@helpers';
import {
  ImageContext,
  ImageUploadImageCropSettingsPayload,
  RequestForDocumentUploadResponse,
  RetrievedDocumentReferenceLink,
} from '../../../../components/v2/components/imageHandling/types/types.d';
import { FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME } from '@v2/components/imageHandling/constants';
import { registerPendingLocalUpload } from '@reducers/v2/imageHandlingV2/messageHandlers/uploadImageDocumentMessageHandler';
import { AppDispatch } from '@store/types';
import { DOC_TYPE } from '@components/imageHandling/common';
import { setOptimisticMachineImageOnUpload } from '@reducers/machines';
import type { Document } from '@api/domain/types';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

interface UploadImageDocumentPayload {
  imageContexts: ImageContext[];
  photo: any;
  tags: Partial<RetrievedDocumentReferenceLink>[];
  documentType: string;
  imageCropSettings?: ImageUploadImageCropSettingsPayload;
  rawContent?: string;
  tempID: string;
  name?: string;
  isSaveCropImageAsNewImageFlow?: boolean;
}

interface CreateBlobInContainerParams {
  containerClient: ContainerClient;
  file: any;
  fileName: string;
  folder: string;
}

interface UploadFileToBlob {
  file: any;
  blobBaseUrl: string;
  sasToken: string;
  blobContainer: string;
  folder: string;
  fileName: string;
  base64?: string;
}

const getBrowser = () => {
  let sBrowser;
  let sUsrAg = window.navigator.userAgent;

  if (sUsrAg.indexOf('Firefox') > -1) {
    sBrowser = 'Mozilla Firefox';
  } else if (sUsrAg.indexOf('Opera') > -1) {
    sBrowser = 'Opera';
  } else if (sUsrAg.indexOf('Trident') > -1) {
    sBrowser = 'Microsoft Internet Explorer';
  } else if (sUsrAg.indexOf('Edge') > -1) {
    sBrowser = 'Microsoft Edge';
  } else if (sUsrAg.indexOf('Chrome') > -1) {
    sBrowser = 'Google Chrome';
  } else if (sUsrAg.indexOf('Safari') > -1) {
    sBrowser = 'Apple Safari';
  } else {
    sBrowser = 'Unknown Browser';
  }

  return sBrowser;
};

const createBlobInContainer = async ({ containerClient, file, fileName, folder }: CreateBlobInContainerParams) => {
  const blobClient = containerClient.getBlockBlobClient(`${folder.replace('/', '')}/${fileName}`);
  const options = { blobHTTPHeaders: { blobContentType: file.type } };
  await blobClient.uploadData(file, options);
};

const uploadFileToBlob = async ({ file, blobBaseUrl, sasToken, blobContainer, folder, fileName, base64 }: UploadFileToBlob) => {
  const blobService = new BlobServiceClient(`${blobBaseUrl}/?${sasToken}`);
  const containerClient = blobService.getContainerClient(`${blobContainer}`);
  if (isWeb) {
    try {
      await createBlobInContainer({ containerClient, file, fileName, folder });
    } catch (e) {
      console.log(e);
    }
  } else {
    try {
      const encodedFileName = encodeURIComponent(fileName);
      const uploadUrl = `${blobBaseUrl}/${blobContainer}/${folder.replace('/', '')}/${encodedFileName}?${sasToken}`;

      await ReactNativeBlobUtil.fetch(
        'PUT',
        uploadUrl,
        {
          'x-ms-blob-type': 'BlockBlob',
          'content-type': 'application/octet-stream',
          'x-ms-blob-content-type': file.type,
          ignoreInterceptor: 'true',
        },
        base64 ?? ReactNativeBlobUtil.wrap(file.uri)
      );
    } catch (e) {
      console.log(e);
    }
  }
};

export const uploadImageDocument = createAsyncThunk<{ documentBlobId: string }, UploadImageDocumentPayload, { dispatch: AppDispatch }>(
  'imageHandlingV2/uploadImageDocument',
  async ({ imageContexts, tempID, photo, tags, documentType, imageCropSettings, rawContent, name, isSaveCropImageAsNewImageFlow }, { dispatch }) => {
    const file = photo.file ? photo.file : photo;
    const originalName = name ?? (file.name === 'undefined' ? photo.name : file.name);

    const uniqueTags = Array.from(new Map(tags.map((t) => [`${t?.scopeId}|${t?.scopeReferenceId}`, t])).values());

    dispatch(
      setOptimisticMachineImageOnUpload({
        tags: uniqueTags as NonNullable<Document['links']>,
        tempID,
        photo,
        file,
      })
    );

    const isDocumentNote = documentType === DOC_TYPE.NOTE;
    const requestForDocumentUploadMethod = isDocumentNote ? 'requestForDocumentUploadV2' : 'requestForDocumentUpload';

    const d: RequestForDocumentUploadResponse = await dispatch(
      backendCall(
        createApiCall((api) => api.imageHandling[requestForDocumentUploadMethod], {
          params: {
            $type: 'new',
            fileName: originalName,
            sourceType: 'App',
          },
        })
      )
    );

    const { blobAccessInfo, documentBlobId, documentBlobName } = d;
    const fileName = documentBlobName;

    registerPendingLocalUpload(documentBlobId, { tempID, imageContexts, isSaveCropImageAsNewImageFlow });

    let base64Data: string | undefined;
    if (!isWeb && rawContent) {
      base64Data = ReactNativeBlobUtil.base64.encode(rawContent);
    } else if (!isWeb && photo.base64) {
      base64Data = photo.base64;
    }

    await uploadFileToBlob({
      file,
      fileName,
      blobBaseUrl: blobAccessInfo.blobBaseUrl,
      sasToken: blobAccessInfo.sasToken,
      blobContainer: blobAccessInfo.blobContainer,
      folder: blobAccessInfo.folders?.find((f) => f.folderType === 'Image')?.folderName || 'file/',
      base64: base64Data,
    });

    let sourceName: string;
    if (isDocumentNote) {
      sourceName = imageSource.App;
    } else if (isWeb) {
      sourceName = getBrowser();
    } else {
      const devName = await DeviceInfo.getDeviceName();
      const model = await DeviceInfo.getModel();
      sourceName = `${model} / ${devName}`;
    }

    const shouldAddCropInfo =
      !!imageCropSettings &&
      !(
        Number(imageCropSettings?.lowerRightXPosition) === 1 &&
        Number(imageCropSettings?.lowerRightYPosition) === 1 &&
        Number(imageCropSettings?.upperLeftXPosition) === 0 &&
        Number(imageCropSettings?.upperLeftYPosition) === 0 &&
        Number(imageCropSettings?.rotationAngle) === 0
      );

    await dispatch(
      backendCall(
        createApiCall((api) => api.imageHandling.processDocument, {
          params: {
            documentBlobId,
            documentType: documentType === 'NcFile' || isDocumentNote ? documentType : 'ImageOrOther',
            documentTags: documentType ? [documentType] : null,
            fileName: originalName,
            sourceName,
            documentLinks: uniqueTags.map((t) => ({ ...t, scopeReferenceId: t.scopeReferenceId, scopeId: Number(t.scopeId) })),
            ...(shouldAddCropInfo ? { imageCropSettings } : {}),
            sourceType: isDocumentNote
              ? imageSource.App
              : isWeb
                ? imageSource.AppUploadFromFileSystem
                : imageSource.AppCameraOrPhotoLibrary,
          },
          onSuccess: async () => {
            setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);
            trackEvent(TRACKING_EVENTS.DOCUMENT, {
              action: 'added',
              documentType: documentType || 'ImageOrOther',
              fileSizeBytes: 0,
              result: 'success',
            });
          },
          onError: (error) => {
            trackEvent(TRACKING_EVENTS.DOCUMENT, {
              action: 'added',
              documentType: documentType || 'ImageOrOther',
              fileSizeBytes: 0,
              result: 'failed',
            });
            console.error(error);
          },
        })
      )
    );

    return { documentBlobId };
  }
);
